import React, { useState } from 'react';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { getProjectCategoryPermissionTypes } from '@/services/item-categories';
import theme from './CategoryPermissionsMatrixEditor.module.scss';
import { Trans } from '@lingui/react/macro';
import CheckboxTableCell from '@/components/generic-controls/CheckboxTableCell';
import { classnames } from '@/utils/html';
import { intersection } from 'lodash-es';
import { useAsyncState } from '@/utils/hooks';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { ProjectCategoryPermission, ItemCategory, LocalizedEnum } from '@/types';
import { useLingui } from '@lingui/react';

interface CategoryPermissionsMatrixEditorProps {
    value: ProjectCategoryPermission[];
    onChange(newValue: ProjectCategoryPermission[]): void;
}

interface HoverState {
    permission?: LocalizedEnum;
    category?: ItemCategory;
    highlightRow?: boolean;
    highlightColumn?: boolean;
}

const CategoryPermissionsMatrixEditor: React.FC<CategoryPermissionsMatrixEditorProps> = ({ value, onChange }) => {
    const [permissions] = useAsyncState(() => getProjectCategoryPermissionTypes(), [], null);
    const categories = useSelector((state: IState) => state.global.itemCategories);
    const [hoverState, setHoverState] = useState<HoverState>({});
    const { _ } = useLingui();

    const handleChange = (category: ItemCategory, permission: LocalizedEnum, checked: boolean) => {
        let newValue;
        if (checked) {
            newValue = [...(value || []), { categoryId: category.id, permission: permission.value }];
        } else {
            newValue = value.filter((x) => !(x.categoryId === category.id && x.permission === permission.value));
        }

        triggerHandler(onChange, newValue);
    };

    const isChecked = (category: ItemCategory, permission: LocalizedEnum) => {
        return !!(value || []).find((x) => x.categoryId === category.id && x.permission === permission.value);
    };

    const areAllCategoriesChecked = (permission: LocalizedEnum) => {
        return (
            intersection(
                value.filter((x) => x.permission === permission.value).map((x) => x.categoryId),
                categories.map((x) => x.id)
            ).length === categories.length
        );
    };

    const changeAllCategoriesChecked = (permission: LocalizedEnum, checked: boolean) => {
        let newValue = value.filter((x) => x.permission !== permission.value);

        if (checked) {
            newValue = [...newValue, ...categories.map((x) => ({ categoryId: x.id, permission: permission.value }))];
        }

        triggerHandler(onChange, newValue);
    };

    const areAllPermissionsChecked = (category: ItemCategory) => {
        return (
            intersection(
                value.filter((x) => x.categoryId === category.id).map((x) => x.permission),
                permissions!.map((x) => x.value)
            ).length === permissions!.length
        );
    };

    const changeAllPermissionsChecked = (category: ItemCategory, checked: boolean) => {
        let newValue = value.filter((x) => x.categoryId !== category.id);

        if (checked) {
            newValue = [...newValue, ...permissions!.map((x) => ({ categoryId: category.id, permission: x.value }))];
        }

        triggerHandler(onChange, newValue);
    };

    if (!categories || !permissions) {
        return null;
    }

    return (
        <table className={theme.matrix}>
            <thead>
                <tr>
                    <td />
                    {categories.map((category) => (
                        <th
                            key={category.id}
                            className={classnames({ [theme.highlight]: category === hoverState.category })}>
                            <div>
                                <span>{category.name}</span>
                            </div>
                        </th>
                    ))}
                    <th className={classnames({ [theme.highlight]: hoverState.highlightRow })}>
                        <div>
                            <span>
                                <Trans>All</Trans>
                            </span>
                        </div>
                    </th>
                </tr>
            </thead>

            <tbody>
                {permissions.map((permission) => (
                    <tr key={permission.value}>
                        <th className={classnames({ [theme.highlight]: permission === hoverState.permission })}>
                            {_({ id: permission.textKey })}
                        </th>
                        {categories.map((category) => (
                            <CheckboxTableCell
                                key={category.id}
                                checked={isChecked(category, permission)}
                                cellClassName={classnames({
                                    [theme.highlight]:
                                        (hoverState.highlightRow && permission === hoverState.permission) ||
                                        (hoverState.highlightColumn && category === hoverState.category),
                                })}
                                onChange={(e) => handleChange(category, permission, e.target.checked)}
                                onMouseEnter={(e) => setHoverState({ category, permission })}
                                onMouseLeave={(e) => setHoverState({})}
                                checkboxClassName={theme.cellCheckbox}
                            />
                        ))}

                        <CheckboxTableCell
                            checked={areAllCategoriesChecked(permission)}
                            cellClassName={classnames({
                                [theme.highlight]: permission === hoverState.permission && hoverState.highlightRow,
                            })}
                            onChange={(e) => changeAllCategoriesChecked(permission, e.target.checked)}
                            onMouseEnter={(e) => setHoverState({ permission, highlightRow: true })}
                            onMouseLeave={(e) => setHoverState({})}
                            checkboxClassName={theme.cellCheckbox}
                        />
                    </tr>
                ))}

                <tr>
                    <th className={classnames({ [theme.highlight]: hoverState.highlightColumn })}>
                        <Trans>All</Trans>
                    </th>
                    {categories.map((category) => (
                        <CheckboxTableCell
                            key={category.id}
                            checked={areAllPermissionsChecked(category)}
                            cellClassName={classnames({
                                [theme.highlight]: category === hoverState.category && hoverState.highlightColumn,
                            })}
                            onChange={(e) => changeAllPermissionsChecked(category, e.target.checked)}
                            onMouseEnter={(e) => setHoverState({ category, highlightColumn: true })}
                            onMouseLeave={(e) => setHoverState({})}
                            checkboxClassName={theme.cellCheckbox}
                        />
                    ))}
                </tr>
            </tbody>
        </table>
    );
};

export default CategoryPermissionsMatrixEditor;
